import { Element } from "@xmpp/xml"

export const AI_WIDGET_MAP_XMLNS = "urn:slickshift:ai-widget:map:0"

export type MapViewMarker = {
  lat: number
  lon: number
  label?: string
  color?: string
  // Semantic marker kind (e.g. "fuel", "pickup", "delivery", "driver").
  // Drives icon selection in AgentMarker. Optional — falls back to color/emoji.
  kind?: string
  // Markdown body shown when the marker is clicked. Optional — markers
  // without a popup just show the inline label on hover. Source comes from
  // a `<popup>...</popup>` child element on the marker stanza.
  popup?: string
}
export type MapViewPolyline = {
  points: Array<{ lat: number; lon: number }>
  color?: string
  width?: number
}
export type MapViewData = {
  markers: MapViewMarker[]
  polylines: MapViewPolyline[]
  chatJid: string
  messageId: string
  // ISO 8601 timestamp from the originating message. Used to order maps
  // chronologically in the slice regardless of arrival order (live vs MAM
  // backfill vs click-to-open on a historical message).
  timestamp: string
  // Human-readable title supplied by the agent, e.g.
  // "Sebastian K. — Stacje paliw". Optional; falls back to a generic label
  // when missing.
  title?: string
}

export function parseAiWidgetMap(
  stanza: Element,
  chatJid: string,
  messageId: string,
  timestamp: string,
): MapViewData | null {
  const el = stanza.getChild("ai-widget-map", AI_WIDGET_MAP_XMLNS)
  if (!el) return null

  const markers: MapViewMarker[] = el.getChildren("marker").flatMap((m) => {
    const lat = parseFloat(m.getAttr("lat"))
    const lon = parseFloat(m.getAttr("lon"))
    if (isNaN(lat) || isNaN(lon)) return []
    const marker: MapViewMarker = { lat, lon }
    const markerLabel = m.getAttr("label")
    if (markerLabel) marker.label = markerLabel
    const color = m.getAttr("color")
    if (color) marker.color = color
    const kind = m.getAttr("kind")
    if (kind) marker.kind = kind
    const popupEl = m.getChild("popup")
    if (popupEl) {
      const popupText = popupEl.text()
      if (popupText) marker.popup = popupText
    }
    return [marker]
  })

  const polylines: MapViewPolyline[] = el.getChildren("polyline").map((p) => {
    const points = p.getChildren("point").flatMap((pt) => {
      const lat = parseFloat(pt.getAttr("lat"))
      const lon = parseFloat(pt.getAttr("lon"))
      if (isNaN(lat) || isNaN(lon)) return []
      return [{ lat, lon }]
    })
    const polyline: MapViewPolyline = { points }
    const color = p.getAttr("color")
    if (color) polyline.color = color
    const widthRaw = p.getAttr("width")
    const widthParsed = widthRaw ? parseInt(widthRaw, 10) : NaN
    if (widthRaw && !isNaN(widthParsed)) polyline.width = widthParsed
    return polyline
  })

  const title = el.getAttr("title") || undefined
  return { markers, polylines, chatJid, messageId, timestamp, title }
}
